import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

interface LgSection {
  number: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  subheading?: string;
}

const TERMS_SECTIONS: LgSection[] = [
  {
    number: '1',
    title: 'Introduction',
    paragraphs: [
      'These Terms &amp; Conditions ("Terms") govern your use of the Sehyog Academy website (sehyog.co.uk), and your booking of, registration for, or attendance at any tuition programme, mock examination, workshop, webinar, or other service offered by Sehyog Academy ("Sehyog", "we", "us" or "our").',
      'By booking a session, creating an account, making a payment, or otherwise using our services, you confirm that you have read, understood, and agree to be bound by these Terms. If you do not agree with any part of these Terms, please do not use our website or services.',
      'Where a service is being booked for a child, the parent or legal guardian making the booking is the party entering into this agreement with Sehyog, and is responsible for the child\'s compliance with these Terms.'
    ]
  },
  {
    number: '2',
    title: 'Who We Are',
    paragraphs: [
      'Sehyog Academy is a UK-based tutoring organisation providing 11+, GCSE, A-Level and university entrance preparation, including tuition, mock examinations, topic tests, and revision resources, delivered both online and face-to-face across our partner centres in England.'
    ]
  },
  {
    number: '3',
    title: 'Bookings and Registration',
    paragraphs: [
      'Bookings for mock tests, tuition programmes, workshops, and webinars can be made through our website, over the phone, or in person at one of our centres. A booking is only confirmed once you receive a confirmation email or SMS from us and, where applicable, payment has been received in full.',
      'You are responsible for ensuring that the information provided at the time of booking — including the student\'s name, year group, school, exam board, and chosen centre or online session — is accurate and up to date. Sehyog is not responsible for a student being unable to attend or being seated for an incorrect paper as a result of incorrect information supplied at booking.',
      'Some programmes (for example the 11+ Summer Intensive or Summer Bootcamp) have limited capacity and are allocated on a first-come, first-served basis. Booking a place does not guarantee a specific tutor, room, or seating position at a centre.'
    ]
  },
  {
    number: '4',
    title: 'Fees, Payment and Refunds',
    paragraphs: [
      'Fees for each course, mock test, or resource are displayed on the relevant booking page at the time of booking. All prices are shown in pounds sterling (GBP) and are inclusive of VAT where applicable, unless stated otherwise.',
      'Payment is required in full at the time of booking unless a payment plan has been explicitly agreed with us in writing. We accept payment by the methods shown at checkout. Where a payment plan or instalment arrangement is offered, missed or failed payments may result in a temporary suspension of access to sessions or materials until the outstanding balance is settled.',
      'Refunds, cancellations, and rescheduling are subject to our separate Payment &amp; Refund Policy, which is available on our website and forms part of these Terms. Where the two documents conflict on a payment-related matter, the Payment &amp; Refund Policy takes precedence.',
      'We reserve the right to change course fees for new bookings at any time. Any change in fees will not affect a booking that has already been confirmed and paid for.'
    ]
  },
  {
    number: '5',
    title: 'Cancellations, Rescheduling and Non-Attendance',
    paragraphs: [
      'If you need to cancel or reschedule a mock test, tuition session, or workshop, please contact us as early as possible. Requests made with reasonable notice will, wherever practical, be accommodated by moving you to an alternative date, centre, or online sitting, subject to availability.',
      'Where a student does not attend a booked session without prior notice ("no-show"), the booking fee is not refunded and the session is treated as used. We may, at our discretion and on a case-by-case basis, offer a one-off transfer to another date.',
      'In the rare event that Sehyog needs to cancel, postpone, or change the format of a session (for example due to venue unavailability, low enrolment, illness of a tutor, or circumstances beyond our reasonable control), we will notify you as soon as possible and offer an alternative date, an equivalent online session, or a full refund of the fee paid for that specific session.'
    ]
  },
  {
    number: '6',
    title: 'Mock Examinations, Results and Reports',
    paragraphs: [
      'Our mock examinations, including the 11+ Mock Test series and GCSE Mini Mock Tests, are designed to closely reflect the format, timing, and difficulty of real entrance and school examinations. They are diagnostic and preparatory tools and are not administered by, endorsed by, or affiliated with any grammar school, consortium, examination board, or local authority admissions process.',
      'Result Analysis Reports (RAR), Cohort Performance Reports, standardised scores, rankings, and percentiles are generated by comparing a student\'s performance against the cohort of students who sat the same paper set with Sehyog. These figures are indicative only, are based on the specific cohort sitting with us at that time, and should not be relied upon as a prediction or guarantee of performance in an actual school entrance examination, GL Assessment, CSSE test, GCSE, or any other official assessment.',
      'Sehyog does not guarantee that a student will be offered a place at any particular school as a result of attending our tuition, mock tests, or other programmes. Outcomes depend on many factors outside our control, including the student\'s own performance on the day, the admissions criteria of the relevant school or local authority, and the number of other applicants.'
    ]
  },
  {
    number: '7',
    title: 'Face-to-Face Sessions: Conduct, Safety and Safeguarding',
    paragraphs: [
      'Where sessions take place at a physical centre, students, parents, and guardians are expected to follow the health, safety, and conduct guidance provided by Sehyog and by the host venue. We reserve the right to refuse entry to, or remove from a session, any individual whose behaviour poses a risk to the safety or wellbeing of other students or staff.',
      'Sehyog takes the safeguarding of children seriously. Staff and tutors engaged by Sehyog are expected to follow appropriate safeguarding practices during all face-to-face and online interactions with students. Parents or guardians should remain contactable for the full duration of any session their child is attending.',
      'For online sessions, parents and guardians are responsible for ensuring the student has a suitable, supervised environment and a stable internet connection. Sehyog is not responsible for a student missing part of a session due to technical issues on the student\'s side, though we will make reasonable efforts to help resolve access problems where we are notified promptly.'
    ]
  },
  {
    number: '8',
    title: 'Website Use and Accounts',
    paragraphs: [
      'You must be at least 18 years old, or have the consent of a parent or legal guardian, to create an account or make a booking on our website. Accounts created on behalf of a student must be managed by a parent, guardian, or other responsible adult.',
      'You are responsible for keeping your login details and account information confidential and for all activity that takes place under your account. Please notify us immediately if you believe your account has been accessed without authorisation.',
      'You agree not to misuse our website, including by attempting to gain unauthorised access to any part of our systems, introducing viruses or other harmful code, or using automated tools to scrape or extract content without our prior written consent.'
    ]
  },
  {
    number: '9',
    title: 'Intellectual Property',
    paragraphs: [
      'All content on the Sehyog website and within our tuition materials — including test papers, question banks, topic tests, worksheets, videos, branding, and the Result Analysis Report format — is owned by or licensed to Sehyog Academy and is protected by copyright and other intellectual property laws.',
      'Materials provided to students and parents are licensed for personal, non-commercial use by the registered student and their immediate family only. You may not copy, reproduce, distribute, sell, or share Sehyog\'s test papers, question banks, or other materials with third parties, including other tutors, schools, or online forums, without our prior written permission.'
    ]
  },
  {
    number: '10',
    title: 'Limitation of Liability',
    paragraphs: [
      'Nothing in these Terms excludes or limits our liability for death or personal injury caused by our negligence, for fraud or fraudulent misrepresentation, or for any other liability that cannot be excluded or limited under English law.',
      'Subject to the above, Sehyog\'s total liability to you in connection with any booking or service, whether in contract, tort (including negligence), or otherwise, is limited to the amount you paid for the specific service giving rise to the claim.',
      'We are not liable for any indirect or consequential loss, including loss of opportunity (such as not being offered a school place), arising from your use of our services.'
    ]
  },
  {
    number: '11',
    title: 'Force Majeure',
    paragraphs: [
      'We will not be liable for any failure or delay in performing our obligations where that failure or delay results from circumstances beyond our reasonable control, including but not limited to extreme weather, industrial action, venue closures, public health restrictions, or failures of third-party internet or telecommunications infrastructure. Where such an event affects a booked session, we will work with you to reschedule or provide an appropriate alternative.'
    ]
  },
  {
    number: '12',
    title: 'Data Protection',
    paragraphs: [
      'Any personal information you provide to us, including information about your child, is handled in accordance with our Privacy Policy, which explains what we collect, why we collect it, and the rights available to you under UK data protection law. The Privacy Policy is available on our website and forms part of these Terms.'
    ]
  },
  {
    number: '13',
    title: 'Changes to These Terms',
    paragraphs: [
      'We may update these Terms from time to time to reflect changes in our services, legal requirements, or operating practices. Where we make material changes, we will update the "Effective date" shown at the top of this page and, where appropriate, notify registered users. Continued use of our services after an update constitutes acceptance of the revised Terms.'
    ]
  },
  {
    number: '14',
    title: 'Governing Law and Jurisdiction',
    paragraphs: [
      'These Terms are governed by the laws of England and Wales. Any dispute arising out of or in connection with these Terms or our services will be subject to the exclusive jurisdiction of the courts of England and Wales, save where mandatory consumer protection law gives you the right to bring proceedings elsewhere.'
    ]
  }
];

@Component({
  selector: 'app-terms-condition',
 imports: [CommonModule],
  templateUrl: './terms-condition.component.html',
  styleUrl: './terms-condition.component.css',
})
export class TermsConditionComponent {
  effectiveDate = '22 August 2026';
  sections: LgSection[] = TERMS_SECTIONS;

  ngOnInit(): void {
    this.loadFonts();
  }

  private loadFonts(): void {
    var self = this;
    var fontId = 'lg-legal-fonts';
    if (document.getElementById(fontId)) {
      return;
    }
    var link = document.createElement('link');
    link.id = fontId;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Nunito:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }
}
